import React, { useEffect, useRef, useState } from "react";
import { wormColors } from "core/src/core/constants.js";
import type { ChatMessage } from "../../online/net";
import { QRCodeSVG } from "qrcode.react";
import { B, Check, Chev, Globe, Keys, Lock, Plus } from "./RoomsUI";
import { useOutside } from "./RuleRows";
import r from "./roomPage.module.scss";
import { ON_PORTAL } from "../../shared/portal-env";
import { portalInviteLink } from "../../shared/portal";

// The room screen's own pieces (around RuleRows): the one Add player drop-down
// of the players column, who can join as a drop-down in the header, and the
// invite card with its QR code.

const sv = { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round" as const, strokeLinejoin: "round" as const, "aria-hidden": true };
const Bot = () => <svg {...sv}><rect x="5" y="8" width="14" height="11" rx="3" /><path d="M12 4v4M9 13h.01M15 13h.01" /></svg>;
const Copy = () => <svg {...sv}><rect x="8" y="8" width="12" height="12" rx="2" /><path d="M16 8V5a1 1 0 00-1-1H5a1 1 0 00-1 1v10a1 1 0 001 1h3" /></svg>;

export type Level = "Easy" | "Medium" | "Hard";
export const LEVELS: Level[] = ["Easy", "Medium", "Hard"];
// The autofill's level as the rules keep it (a skill from 1 to 10) and back.
export const SKILL_OF: Record<Level, number> = { Easy: 2, Medium: 5, Hard: 8 };
export const levelOf = (skill: number): Level => (skill <= 3 ? "Easy" : skill <= 7 ? "Medium" : "Hard");

// "Add player": everything that puts someone in a seat, in one drop-down that
// stays open while you add (several bots in a row) and closes with Close,
// a press outside it, or the button again.
//   local    someone on this computer (with the keys they will get), when possible
//   bot      a bot of a level, now (the host, a local game)
//   fill     the level the empty seats are filled with at the start (the host)
// When nobody more fits, it says so at its top (and, for the host, offers
// another seat right there), and what cannot be pressed is plainly off.
export function AddPlayer({ local, bot, fill, disabled, full }: {
  local?: { onAdd: () => void; keys?: string | null; off?: string | null };
  bot?: { onAdd: (level: Level) => void; off?: boolean };
  fill?: { level: Level | null; onPick: (level: Level | null) => void };
  disabled?: boolean;
  full?: { text: string; onMore?: () => void } | null; // every seat is taken
}) {
  const [open, setOpen] = useState(false);
  const ref = useOutside(open, () => setOpen(false));
  return (
    <div className={r.anchor} ref={ref}>
      <B className={`${r.addBtn} ${open ? r.addOpen : ""}`} onClick={() => setOpen((o) => !o)} disabled={disabled} aria-expanded={open}><Plus />Add player</B>
      {open && (
        <div className={`${r.pop} ${r.addPop}`}>
          {full && (
            <div className={r.full}>
              <span>{full.text}</span>
              {full.onMore && <B className={r.fullMore} onClick={full.onMore}><Plus />Another seat</B>}
            </div>
          )}
          {local && (
            <B className={r.choice} onClick={local.onAdd} disabled={!!local.off}>
              <span className={r.choiceIcon}><Keys /></span>
              <span><b>Player on this computer</b>{local.off && !full ? <small>{local.off}</small> : null}</span>
            </B>
          )}
          {bot && (<>
            <small className={r.sub}>{local ? "Or a bot" : "A bot"}</small>
            <div className={r.grid} style={{ gridTemplateColumns: "repeat(3, minmax(0, 1fr))" }}>
              {LEVELS.map((l) => <B key={l} className={`${r.opt} ${r.optBot}`} onClick={() => bot.onAdd(l)} disabled={bot.off}><Bot />{l}</B>)}
            </div>
          </>)}
          {fill && (<>
            <small className={r.sub}>Autofill with bots</small>
            <div className={r.grid} style={{ gridTemplateColumns: "repeat(4, minmax(0, 1fr))" }}>
              {([null, ...LEVELS] as (Level | null)[]).map((l) => (
                <B key={l || "no"} className={`${r.opt} ${fill.level === l ? r.optOn : ""}`} style={{ "--tc": "#f5c542" } as React.CSSProperties} onClick={() => fill.onPick(l)} aria-pressed={fill.level === l}>{l || "No"}</B>
              ))}
            </div>
          </>)}
          <B className={r.popDone} onClick={() => setOpen(false)}>Close</B>
        </div>
      )}
    </div>
  );
}

// Who can join, in the room's header. The host changes it in place; a guest
// only reads it.
export type Kind = "private" | "public" | "local";
const KINDS: { id: Kind; name: string; line: string; icon: () => React.ReactElement; color: string }[] = [
  { id: "private", name: "Private", line: "Only people with the code or link", icon: Lock, color: "#ff9f45" },
  { id: "public", name: "Public", line: "Listed in open rooms, anyone can join", icon: Globe, color: "#19f0b0" },
  { id: "local", name: "Local", line: "Everyone plays on this device", icon: Keys, color: "#4fa8ff" },
];
export function KindMenu({ kind, onPick, offer = ["private", "public", "local"], right }: { kind: Kind; onPick?: (k: Kind) => void; offer?: Kind[]; right?: boolean }) {
  const [open, setOpen] = useState(false);
  const ref = useOutside(open, () => setOpen(false));
  const now = KINDS.find((k) => k.id === kind) || KINDS[0];
  const style = { "--kc": now.color } as React.CSSProperties;
  if (!onPick) return <span className={`${r.kindBtn} ${r.kindStatic}`} style={style}><now.icon />{now.name}</span>;
  return (
    <div className={r.anchor} ref={ref}>
      <B className={`${r.kindBtn} ${open ? r.kindOpen : ""}`} style={style} onClick={() => setOpen((o) => !o)} aria-expanded={open}><now.icon />{now.name}<span className={r.kindChev}><Chev /></span></B>
      {open && (
        <div className={`${r.pop} ${r.kindPop} ${right ? r.kindPopRight : ""}`}>
          {KINDS.filter((k) => offer.includes(k.id)).map((k) => (
            <B key={k.id} className={`${r.kindOpt} ${k.id === kind ? r.kindOn : ""}`} style={{ "--kc": k.color } as React.CSSProperties} onClick={() => { setOpen(false); if (k.id !== kind) onPick(k.id); }}>
              <span className={r.choiceIcon}><k.icon /></span><span><b>{k.name}</b><small>{k.line}</small></span>
            </B>
          ))}
        </div>
      )}
    </div>
  );
}

// Every box of the room screen is a Panel: one background, one border, one
// rounding (roomPage.module.scss .card). The seats and the chat go in one bare
// (their `bare` prop), so no box brings its own corners.
export function Panel({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div className={`${r.card} ${className || ""}`}>{children}</div>;
}

// The invite card, as the room had it before October: the QR code, the code
// itself (pressed, it is copied and turns green), and the share link in a
// field with a copy button (the field turns green when it is copied).
function CopyRow({ label, value, display }: { label: string; value: string; display?: string }) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 1400);
    return () => clearTimeout(t);
  }, [copied]);
  const copy = () => { try { navigator.clipboard?.writeText(value); setCopied(true); } catch { /* clipboard blocked */ } };
  return (
    <div className={r.copyRow}>
      <small className={r.sub}>{label}</small>
      <div className={r.copyLine}>
        <span className={`${r.copyField} ${copied ? r.copied : ""}`} title={value}><span>{display ?? value}</span></span>
        <B className={r.copyBtn} onClick={copy} title="Copy" aria-label={`Copy the ${label.toLowerCase()}`}>{copied ? <Check /> : <Copy />}</B>
      </div>
    </div>
  );
}
export function InviteCard({ code }: { code: string }) {
  const origin = typeof window !== "undefined" ? window.location.origin : "https://achtungroyale.io";
  const link = `${origin}/lobby/${code}`;
  const [siteLink] = useState(() => (ON_PORTAL ? portalInviteLink(code) : null));
  // (on a game site's build the QR code and the link are the site's own, into this room)
  const shared = ON_PORTAL ? siteLink : link;
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 1400);
    return () => clearTimeout(t);
  }, [copied]);
  const copyCode = () => { try { navigator.clipboard?.writeText(code); setCopied(true); } catch { /* clipboard blocked */ } };
  return (
    <Panel className={r.invite}>
      <div className={r.cardHead}>Invite</div>
      {/* (a game site's invite link is long: a bigger code with fewer, larger
          squares, so a phone across the room reads it) */}
      {shared && <div className={`${r.qr} ${ON_PORTAL ? r.qrBig : ""}`}><QRCodeSVG value={shared} size={ON_PORTAL ? 210 : 140} bgColor="#ffffff" fgColor="#0b0f1a" level={ON_PORTAL ? "L" : "M"} /></div>}
      <B className={`${r.code} ${copied ? r.codeCopied : ""}`} onClick={copyCode} title="Copy code" aria-label="Copy the code"><span>{code}</span><Copy /></B>
      {shared && <CopyRow label="Share link" value={shared} display={shared.replace(/^https?:\/\//, "")} />}
    </Panel>
  );
}

// The room's chat, as it was before October: "Name: text" lines, the name in
// the player's colour, and a field that lights up while you type.
export function ChatCard({ chat, onSend }: { chat?: ChatMessage[]; onSend?: (text: string) => void }) {
  const [draft, setDraft] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [chat?.length]);
  const send = () => {
    const t = draft.trim();
    if (!t || !onSend) return;
    onSend(t);
    setDraft("");
  };
  return (
    <Panel className={r.chat}>
      <div className={r.cardHead}>Chat</div>
      <div className={r.msgs} ref={scrollRef}>
        {!chat || chat.length === 0 ? <div className={r.chatEmpty}>Say hi to the room.</div> : chat.map((m, i) => (m.system
          ? <div key={i} className={r.sysMsg}>{m.text}</div>
          : <div key={i} className={r.msg}><b style={{ color: (m.colorId && (wormColors as any)[m.colorId]) || "#28c4d4" }}>{m.name || "Player"}</b><span className={r.msgColon}>: </span><span>{m.text}</span></div>))}
      </div>
      <form className={r.chatIn} onSubmit={(e) => { e.preventDefault(); send(); }}>
        <input value={draft} onChange={(e) => setDraft(e.target.value.slice(0, 300))} placeholder="Message" aria-label="Chat message" disabled={!onSend} />
        <B type="submit" className={draft.trim() ? r.sendOn : ""} disabled={!draft.trim()}>Send</B>
      </form>
    </Panel>
  );
}

// The room's three columns: the players, the rules with Start under them, and
// (an online room) the invite and the chat. A phone held sideways shows one at a
// time behind three tabs, Start always in view.
const TabPlayers = () => <svg {...sv}><circle cx="9" cy="8" r="3.5" /><path d="M2.5 20a6.5 6.5 0 0113 0M16 4.8a3.5 3.5 0 010 6.4M18 14.6a6.5 6.5 0 013.5 5.4" /></svg>;
const TabRules = () => <svg {...sv}><path d="M4 7h9M17 7h3M4 17h3M11 17h9" /><circle cx="15" cy="7" r="2" /><circle cx="9" cy="17" r="2" /></svg>;
const TabInvite = () => <svg {...sv}><path d="M4 5h16v11H10l-5 4v-4H4V5z" /></svg>;
export function RoomLayout({ land, players, rules, start, side, sideDot, firstTab = "rules" }: {
  land: boolean; players: React.ReactNode; rules: React.ReactNode; start: React.ReactNode; side?: React.ReactNode; sideDot?: boolean;
  firstTab?: "players" | "rules" | "side"; // the tab a phone opens on
}) {
  const [tab, setTab] = useState<"players" | "rules" | "side">(firstTab);
  if (land) {
    const tabs: { id: "players" | "rules" | "side"; label: string; icon: () => React.ReactElement }[] = [
      { id: "players", label: "Players", icon: TabPlayers }, { id: "rules", label: "Rules", icon: TabRules },
      ...(side ? [{ id: "side" as const, label: "Invite", icon: TabInvite }] : []),
    ];
    return (
      <div className={r.land}>
        <div className={r.tabs}>
          {tabs.map((t) => (
            <B key={t.id} className={`${r.tab} ${tab === t.id ? r.tabOn : ""}`} onClick={() => setTab(t.id)} aria-pressed={tab === t.id}>
              <t.icon /><span>{t.label}</span>{t.id === "side" && sideDot && tab !== "side" && <i className={r.tabDot} />}
            </B>
          ))}
        </div>
        <div className={r.landBody}>
          {tab === "players" ? <div className={r.landPlayers}>{players}</div> : tab === "side" ? <div className={r.landPair}>{side}</div> : <Panel className={r.mid}>{rules}</Panel>}
          {start}
        </div>
      </div>
    );
  }
  return (
    <div className={r.layout}>
      <div className={r.col}>{players}</div>
      <div className={r.col}><Panel className={r.mid}>{rules}</Panel>{start}</div>
      {/* (the local game has no invite or chat: its column stays, empty, so the
          rules are as wide as in a room) */}
      <div className={r.col} aria-hidden={side ? undefined : true}>{side}</div>
    </div>
  );
}
